import React, { useCallback, useEffect, useState } from "react";
import AddSubCategory from "../addSubCategory/AddSubCategory";
import { findAllSubCategory } from "../../../../api/inventoryService";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CategoryIcon from "@mui/icons-material/Category";
import SearchIcon from "@mui/icons-material/Search";

interface SubCategory {
  _id: string;
  subCategoryName: string;
  categoryId?: {
    _id: string;
    categoryName: string;
  };
}

const AllSubCategory: React.FC = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [data, setData] = useState<SubCategory[]>([]);
  const [id, setId] = useState<string>("");
  const [search, setSearch] = useState("");

  const getData = useCallback(() => {
    findAllSubCategory()
      .then((data) => setData(data as SubCategory[]))
      .catch((error) => console.error(error?.response));
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  const closeModal = useCallback(() => {
    setOpen(false);
    setId("");
    getData();
  }, [getData]);

  const openModal = useCallback(() => setOpen(true), []);

  const update = useCallback((id: string) => {
    setId(id);
    setOpen(true);
  }, []);

  const filteredData = data.filter(
    (item) =>
      item.subCategoryName.toLowerCase().includes(search.toLowerCase()) ||
      item.categoryId?.categoryName?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-3 mb-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2.5">
          <div className="relative flex-1 max-w-md">
            <SearchIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              sx={{ fontSize: 16 }}
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search sub categories..."
              className="w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
            />
          </div>
          <div className="flex items-center gap-2.5">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/40 rounded-lg">
              <CategoryIcon className="text-emerald-500 dark:text-emerald-300" sx={{ fontSize: 16 }} />
              <span className="font-bold text-xs text-emerald-600 dark:text-emerald-300">{data.length}</span>
              <span className="text-slate-500 dark:text-slate-400 text-[10px]">Sub Categories</span>
            </div>
            <button
              onClick={openModal}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all font-medium text-xs"
            >
              <AddIcon sx={{ fontSize: 16 }} />
              Add Sub Category
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
        <div className="md:hidden p-3 space-y-2">
          {filteredData.length === 0 ? (
            <div className="text-center py-8 text-slate-400 dark:text-slate-500">
              <CategoryIcon sx={{ fontSize: 36 }} className="mb-2" />
              <p className="text-xs">No sub categories found</p>
            </div>
          ) : (
            filteredData.map((item, i) => (
              <div key={item._id} className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 border border-slate-100 dark:border-slate-600">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-xs text-slate-800 dark:text-slate-100">{item.subCategoryName}</span>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                      {item.categoryId?.categoryName ?? "—"}
                    </p>
                  </div>
                  <button
                    onClick={() => update(item._id)}
                    className="p-1.5 bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 rounded-md hover:bg-emerald-100 dark:hover:bg-emerald-900/60 transition-colors"
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-600">
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">No</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Sub Category</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Category</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-slate-400 dark:text-slate-500">
                    <CategoryIcon sx={{ fontSize: 36 }} className="mb-2" />
                    <p className="text-xs">No sub categories found</p>
                  </td>
                </tr>
              ) : (
                filteredData.map((item, i) => (
                  <tr key={item._id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-3 py-2">
                      <span className="px-2 py-0.5 text-[10px] font-semibold rounded-md bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300">
                        {i + 1}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="font-medium text-xs text-slate-800 dark:text-slate-100">{item.subCategoryName}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="px-2 py-0.5 text-[10px] font-medium rounded-md bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                        {item.categoryId?.categoryName ?? "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => update(item._id)}
                        className="p-1.5 rounded-md bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 transition-colors"
                        title="Edit"
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {open && <AddSubCategory id={id} closeModal={closeModal} />}
    </>
  );
};

export default AllSubCategory;
